import React, { useCallback, useEffect, useState } from "react";
import { SearchIcon } from "../components/Icons";
import { buildScoringTiers } from "../utils";

// Tier thresholds come from the recommended-threshold sheet
// ("Deals and Packages" column).
const scoringTiers = buildScoringTiers("PACKAGES", [
	{
		key: "bestMatch",
		label: "Exact Match",
		meaning: "Packages that exactly match your requirements.",
		warning: false,
	},
	{
		key: "strongMatch",
		label: "Strong Match",
		meaning: "Very relevant package results.",
		warning: false,
	},
	{
		key: "acceptable",
		label: "Good Match",
		meaning: "Packages are generally relevant.",
		warning: false,
	},
	{
		key: "extended",
		label: "Broad Match",
		meaning: "Includes packages with partial relevance.",
		warning: true,
	},
	{
		key: "maximumReach",
		label: "All Results",
		meaning: "Shows all qualifying packages; many may have low relevance.",
		warning: true,
	},
]);

export const PackageSearchInterface = ({
	// Search state props (from parent/hook)
	searchQuery,
	searchTerms,
	excludeTerms,
	isSearching,
	searchError,
	filters,
	performSearch,

	// Search handlers (from parent/hook)
	onSearchInputChange,
	addSearchTerm,
	removeSearchTerm,
	clearSearchTerms,
	triggerSearch,

	// Missing state setters that are used in the component
	setSearchQuery,
	setSearchTerms,
	setExcludeTerms,

	initialSortBy = "compositeScore",
	initialView = "DESC",
	onSortChange,

	// Optional result handlers
	searchResults,
	onSearchResults,
	onSearchError,

	className = "",
}) => {
	const [selectedTier, setSelectedTier] = useState(scoringTiers[1]); // Default to "Strong Match"

	// Package-specific sort options
	const [sortBy, setSortBy] = useState(initialSortBy);
	const [view, setView] = useState(initialView || "DESC");

	useEffect(() => {
		console.log("📊 PackageSearchInterface: Syncing sort from props:", {
			initialSortBy,
			initialView,
		});
		setSortBy(initialSortBy);
		setView(initialView);
	}, [initialSortBy, initialView]);

	const sortOptions = [
		{ value: "compositeScore", label: "Composite Score" },
		{ value: "impressions", label: "Impressions" },
		{ value: "similarity", label: "Relevance" },
		{ value: "viewability", label: "Viewability" },
		{ value: "createdDate", label: "Created Date" },
		{ value: "dealPrice", label: "Deal Price" },
		{ value: "packageName", label: "Package Name" },
		{ value: "clickThroughRate", label: "CTR" },
		{ value: "revenuePerImpression", label: "Revenue per Impression" },
		{ value: "costPerImpression", label: "Cost per Impression" },
		{ value: "vtr", label: "VTR" },
		{ value: "totalMarketRequests", label: "Market Requests" },
		{ value: "oxWinRate", label: "OpenX win rate" },
		{ value: "pubWinRate", label: "Pub Win rate" },
		{ value: "bidCpm", label: "Bid CPM" },
		{ value: "bidGuidance", label: "Bid Guidance" },
	];

	// Pass results up to parent when they change
	useEffect(() => {
		if (searchResults && onSearchResults) {
			onSearchResults(searchResults);
		}
	}, [searchResults, onSearchResults]);

	// Pass errors up to parent when they change
	useEffect(() => {
		if (searchError && onSearchError) {
			onSearchError(searchError);
		}
	}, [searchError, onSearchError]);

	const handleSearchInputChange = useCallback(
		(e) => {
			if (onSearchInputChange) {
				onSearchInputChange(e);
			} else if (setSearchQuery) {
				setSearchQuery(e.target.value);
			}
		},
		[onSearchInputChange, setSearchQuery],
	);

	const handleAddSearchTerm = useCallback(
		(query, options = {}) => {
			const { threshold, originalScore, type = "include" } = options;

			const newTerm = {
				query: query,
				text: query,
				threshold: threshold ?? selectedTier.threshold,
				originalScore: originalScore || selectedTier.score,
			};

			if (addSearchTerm) {
				addSearchTerm(query, options);
			} else {
				// Fallback to direct state manipulation
				if (type === "include" && setSearchTerms) {
					setSearchTerms((prev) => {
						// Check if term already exists
						if (prev.some((term) => term.query === query)) {
							return prev;
						}
						return [...prev, newTerm];
					});
				} else if (type === "exclude" && setExcludeTerms) {
					setExcludeTerms((prev) => {
						// Check if term already exists
						if (prev.some((term) => term.query === query)) {
							return prev;
						}
						return [...prev, newTerm];
					});
				}
			}

			// Clear the input
			if (setSearchQuery) {
				setSearchQuery("");
			}
		},
		[
			selectedTier,
			addSearchTerm,
			setSearchTerms,
			setExcludeTerms,
			setSearchQuery,
		],
	);

	const handleRemoveSearchTerm = useCallback(
		(query) => {
			if (removeSearchTerm) {
				removeSearchTerm(query);
			} else {
				// Fallback to direct state manipulation
				if (setSearchTerms) {
					setSearchTerms((prev) => prev.filter((term) => term.query !== query));
				}
				if (setExcludeTerms) {
					setExcludeTerms((prev) =>
						prev.filter((term) => term.query !== query),
					);
				}
			}
		},
		[removeSearchTerm, setSearchTerms, setExcludeTerms],
	);

	const handleClearSearchTerms = useCallback(() => {
		if (clearSearchTerms) {
			clearSearchTerms();
		} else {
			// Fallback to direct state manipulation
			if (setSearchTerms) {
				setSearchTerms([]);
			}
			if (setExcludeTerms) {
				setExcludeTerms([]);
			}
		}
	}, [clearSearchTerms, setSearchTerms, setExcludeTerms]);

	const handleTriggerSearch = useCallback(
		async (searchOptions = {}) => {
			console.log("🔍 Package search triggered with options:", searchOptions);

			if (triggerSearch) {
				return await triggerSearch(searchOptions);
			} else if (performSearch) {
				const searchParams = {
					searchTerms,
					excludeTerms,
					filters,
					sortBy: searchOptions.sortBy || sortBy,
					view: searchOptions.view || view,
					startIndex: searchOptions.startIndex || 0,
					noOfResult: searchOptions.noOfResult || 100,
					noOfDays: searchOptions.noOfDays || null,
				};

				console.log("📦 Calling performSearch with params:", searchParams);

				try {
					const result = await performSearch(searchParams);
					return result;
				} catch (error) {
					console.error("Search failed:", error);
					throw error;
				}
			}
		},
		[
			searchTerms,
			excludeTerms,
			filters,
			sortBy,
			view,
			performSearch,
			triggerSearch,
		],
	);

	const handleAddTerm = useCallback(
		(termType) => {
			if (searchQuery && searchQuery.trim()) {
				// The tier already carries the recommended cosine distance.
				handleAddSearchTerm(searchQuery.trim(), {
					threshold: selectedTier.threshold,
					originalScore: selectedTier.score,
					type: termType,
				});
			}
		},
		[searchQuery, selectedTier, handleAddSearchTerm],
	);

	const handleSearchClick = useCallback(async () => {
		console.log("🔍 Package search button clicked - triggering API call");
		console.log("📊 Current search terms:", [
			...(searchTerms || []),
			...(excludeTerms || []),
		]);
		console.log("🎯 Using sort from interface:", { sortBy, view });

		try {
			const searchOptions = {
				sortBy,
				view,
				overrideFilterSort: true,
			};

			await handleTriggerSearch(searchOptions);
			console.log("✅ Package search completed successfully");
		} catch (error) {
			console.error("❌ Package search failed:", error);
		}
	}, [handleTriggerSearch, searchTerms, excludeTerms, sortBy, view]);

	const handleSearchKeyPress = useCallback(
		(e) => {
			if (e.key === "Enter") {
				e.preventDefault();
				handleAddTerm("include");
			}
		},
		[handleAddTerm],
	);

	const handleTierChange = useCallback((tier) => {
		setSelectedTier(tier);
	}, []);

	const handleSortByChange = (e) => {
		const newSortBy = e.target.value;
		console.log("Package Search: Sort by changed to:", newSortBy);
		setSortBy(newSortBy);
		if (onSortChange) {
			onSortChange(newSortBy, view);
		}
	};

	const handleSortOrderToggle = () => {
		const newOrder = view === "ASC" ? "DESC" : "ASC";
		console.log("Package Search: Sort order changed to:", newOrder);
		setView(newOrder);
		if (onSortChange) {
			onSortChange(sortBy, newOrder);
		}
	};

	// TierDropdown component
	const TierDropdown = ({ selectedTier, onTierChange, scoringTiers }) => {
		const [showTooltip, setShowTooltip] = useState(false);

		const handleTierSelect = (e) => {
			const tierLabel = e.target.value;
			const tier = scoringTiers.find((t) => t.label === tierLabel);
			onTierChange(tier);
		};

		return (
			<div className="tier-dropdown-container">
				<div className="tier-dropdown-wrapper">
					<select
						value={selectedTier.label}
						onChange={handleTierSelect}
						className="tier-simple-select"
					>
						{scoringTiers.map((tier) => (
							<option key={tier.label} value={tier.label}>
								{tier.label}
							</option>
						))}
					</select>

					<div
						className="info-icon-container"
						onMouseEnter={() => setShowTooltip(true)}
						onMouseLeave={() => setShowTooltip(false)}
					>
						<span className="info-icon">i</span>

						{showTooltip && (
							<div className="tier-tooltip">
								<div className="tooltip-header">
									<strong>Package Scoring Tiers</strong>
								</div>
								<div className="tooltip-content">
									{scoringTiers.map((tier, index) => (
										<div
											key={index}
											className={`tooltip-tier ${tier.warning ? "warning" : ""}`}
										>
											<div className="tooltip-tier-header">
												<span className="tooltip-tier-label">{tier.label}</span>
												<span className="tooltip-tier-score">
													{tier.score}+
												</span>
											</div>
											<div className="tooltip-tier-meaning">
												{tier.warning && (
													<span className="warning-indicator">⚠️ </span>
												)}
												{tier.meaning}
											</div>
										</div>
									))}
								</div>
							</div>
						)}
					</div>
				</div>
			</div>
		);
	};

	// Combine include and exclude terms for display
	const allTerms = [
		...(searchTerms || []).map((term) => ({ ...term, type: "include" })),
		...(excludeTerms || []).map((term) => ({ ...term, type: "exclude" })),
	];

	const formatArrayDisplay = (array, maxLength = 60, maxItems = 3) => {
		if (!array || array.length === 0) return null;

		if (array.length === 1) {
			return array[0];
		}

		let result = array[0];
		let itemCount = 1;

		for (let i = 1; i < array.length && itemCount < maxItems; i++) {
			const nextItem = array[i];
			const testResult = result + ", " + nextItem;

			if (testResult.length <= maxLength) {
				result = testResult;
				itemCount++;
			} else {
				break;
			}
		}

		if (itemCount < array.length) {
			const remaining = array.length - itemCount;
			result += ` (+${remaining} more)`;
		}

		return result;
	};

	// Helper function to format metric values for packages
	const formatMetricValue = (value, type) => {
		if (!value || value === 0) return null;

		switch (type) {
			case "downloads":
				return value >= 1000000
					? `${(value / 1000000).toFixed(1)}M`
					: value >= 1000
						? `${(value / 1000).toFixed(0)}K`
						: value.toString();
			case "size":
				return value >= 1024
					? `${(value / 1024).toFixed(1)}GB`
					: value >= 1
						? `${value.toFixed(1)}MB`
						: `${(value * 1024).toFixed(0)}KB`;
			case "currency":
				return value >= 1000000
					? `$${(value / 1000000).toFixed(1)}M`
					: value >= 1000
						? `$${(value / 1000).toFixed(0)}K`
						: `$${value}`;
			case "percentage":
				return `${value.toFixed(2)}%`;
			case "rating":
				return `${value.toFixed(1)}/5.0`;
			default:
				return value.toString();
		}
	};

	return (
		<div className={`package-search-interface ${className}`}>
			{/* Search Terms Display */}
			{allTerms.length > 0 && (
				<div className="search-terms-display">
					<div className="search-terms-content">
						<div className="current-search-term">
							<span>Current search: </span>
							{allTerms.map((term, index) => (
								<span key={`${term.type}-${term.query}-${index}`}>
									<span
										className={`search-term ${term.type === "include" ? "term-include" : "term-exclude"}`}
									>
										{term.type === "include" ? "+" : "-"}
										{term.query} (
										{term.originalScore ||
											Math.round((term.threshold || 0) * 100)}
										)
										<button
											onClick={() => {
												console.log("🗑️ Removing term:", term);
												handleRemoveSearchTerm(term.query);
											}}
											className="term-remove-btn"
										>
											×
										</button>
									</span>
									{index < allTerms.length - 1 && <span> </span>}
								</span>
							))}
						</div>
					</div>
					<button
						className="btn-clear-all-inline"
						onClick={handleClearSearchTerms}
					>
						Clear All
					</button>
				</div>
			)}

			{/* Main Search Row */}
			<div className="search-input-row">
				<div className="search-input-wrapper">
					<div className="search-icon-container">
						<SearchIcon size={16} />
					</div>
					<input
						type="text"
						placeholder="Search for packages by name, category, functionality, or features..."
						value={searchQuery || ""}
						onChange={handleSearchInputChange}
						onKeyPress={handleSearchKeyPress}
						className="search-input"
					/>

					<div className="term-type-buttons">
						<button
							type="button"
							onClick={() => handleAddTerm("include")}
							className="term-type-btn include-btn"
							disabled={!searchQuery || !searchQuery.trim()}
						>
							Include
						</button>
						<button
							type="button"
							onClick={() => handleAddTerm("exclude")}
							className="term-type-btn exclude-btn"
							disabled={!searchQuery || !searchQuery.trim()}
						>
							Exclude
						</button>
					</div>
				</div>

				<div className="tier-input-container">
					<TierDropdown
						selectedTier={selectedTier}
						onTierChange={handleTierChange}
						scoringTiers={scoringTiers}
					/>
				</div>

				<button
					type="button"
					onClick={handleSearchClick}
					className="search-button"
					disabled={isSearching}
				>
					{isSearching ? (
						<>
							<div className="loading-spinner-small"></div>
							Searching...
						</>
					) : (
						<>Search</>
					)}
				</button>
			</div>

			{/* Sort Section */}
			<div className="performance-filter-section">
				<div className="performance-filter-row">
					<div className="sort-container">
						<div className="performance-label-container">
							<label className="performance-filter-label">Sort by</label>
						</div>
						<div className="sort-controls">
							<select
								value={sortBy}
								onChange={handleSortByChange}
								className="sort-select"
							>
								{sortOptions.map((option) => (
									<option key={option.value} value={option.value}>
										{option.label}
									</option>
								))}
							</select>
							<span
								onClick={handleSortOrderToggle}
								className="sort-order-arrow"
								title={view === "DESC" ? "Descending" : "Ascending"}
							>
								{view === "DESC" ? "↓" : "↑"}
							</span>
						</div>
					</div>
				</div>
			</div>

			{/* Quick Filters Section */}
			<div className="quick-filters">
				{/* Sort Indicator */}
				<span className="quick-filter filter-basic sort-indicator">
					Sort:{" "}
					{sortOptions.find((opt) => opt.value === sortBy)?.label || sortBy} (
					{view === "DESC" ? "High to Low" : "Low to High"})
				</span>

				{/* Package Category Filters */}
				{filters?.packageCategoryL1?.length > 0 && (
					<span className="quick-filter filter-category">
						L1 Category: {formatArrayDisplay(filters.packageCategoryL1, 50)}
					</span>
				)}

				{filters?.packageCategoryL2?.length > 0 && (
					<span className="quick-filter filter-category">
						L2 Category: {formatArrayDisplay(filters.packageCategoryL2, 50)}
					</span>
				)}

				{filters?.packageCategoryL3?.length > 0 && (
					<span className="quick-filter filter-category">
						L3 Category: {formatArrayDisplay(filters.packageCategoryL3, 50)}
					</span>
				)}

				{filters?.primaryVertical?.length > 0 && (
					<span className="quick-filter filter-platform">
						Vertical: {formatArrayDisplay(filters.primaryVertical, 50)}
					</span>
				)}

				{filters?.country?.length > 0 && (
					<span className="quick-filter filter-type">
						Country: {formatArrayDisplay(filters.country, 40)}
					</span>
				)}

				{filters?.deviceTypes?.length > 0 && (
					<span className="quick-filter filter-license">
						Devices: {formatArrayDisplay(filters.deviceTypes, 40)}
					</span>
				)}

				{filters?.adPlacement?.length > 0 && (
					<span className="quick-filter filter-attributes">
						Ad Placement: {formatArrayDisplay(filters.adPlacement, 40)}
					</span>
				)}

				{filters?.distributionChannel?.length > 0 && (
					<span className="quick-filter filter-attributes">
						Distribution: {formatArrayDisplay(filters.distributionChannel, 40)}
					</span>
				)}

				{filters?.dealName?.length > 0 && (
					<span className="quick-filter filter-financial">
						Deal: {formatArrayDisplay(filters.dealName, 40)}
					</span>
				)}

				{filters?.audienceName?.length > 0 && (
					<span className="quick-filter filter-version">
						Audience: {formatArrayDisplay(filters.audienceName, 40)}
					</span>
				)}

				{filters?.audienceProviderName?.length > 0 && (
					<span className="quick-filter filter-metrics">
						Provider: {formatArrayDisplay(filters.audienceProviderName, 40)}
					</span>
				)}

				{filters?.impressions > 0 && (
					<span className="quick-filter filter-metrics">
						Impressions: {filters.impressions}
					</span>
				)}

				{filters?.totalSpendInUSD > 0 && (
					<span className="quick-filter filter-metrics">
						Spend: {filters.totalSpendInUSD}
					</span>
				)}

				{filters?.bidCpm > 0 && (
					<span className="quick-filter filter-metrics">
						Bid CPM: {filters.bidCpm}
					</span>
				)}

				{filters?.viewability > 0 && (
					<span className="quick-filter filter-metrics">
						Viewability: {filters.viewability}
					</span>
				)}
			</div>

			{/* Error Display */}
			{searchError && (
				<div className="error-display">
					<strong>Search Error:</strong> {searchError}
				</div>
			)}

			<style jsx>{`
        .package-search-interface {
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 20px;
          background: white;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          width: 100%;
          box-sizing: border-box;
        }

        .search-terms-display {
          margin-bottom: 12px;
          padding: 8px 12px;
          background: #f9fafb;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          min-height: 36px;
          max-height: 48px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          overflow: hidden;
          gap: 12px;
        }

        .search-terms-content {
          flex: 1;
          overflow: hidden;
        }

        .current-search-term {
          font-size: 13px;
          color: #374151;
          line-height: 1.3;
          display: -webkit-box;
          -webkit-line-clamp: 1;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .search-term {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 4px 8px;
          margin: 2px;
          border-radius: 4px;
          font-size: 13px;
          font-weight: 500;
        }

        .term-include {
          background: #dcfce7;
          color: #166534;
          border: 1px solid #bbf7d0;
        }

        .term-exclude {
          background: #fee2e2;
          color: #991b1b;
          border: 1px solid #fecaca;
        }

        .term-remove-btn {
          margin-left: 4px;
          background: none;
          border: none;
          color: inherit;
          cursor: pointer;
          font-weight: bold;
          padding: 0 2px;
          border-radius: 2px;
          transition: background-color 0.2s;
        }

        .term-remove-btn:hover {
          background: rgba(0, 0, 0, 0.1);
        }

        .btn-clear-all-inline {
          padding: 4px 12px;
          background: #ef4444;
          color: white;
          border: none;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
          white-space: nowrap;
          flex-shrink: 0;
        }

        .btn-clear-all-inline:hover {
          background: #dc2626;
        }

        .search-input-row {
          display: flex;
          align-items: flex-end;
          gap: 12px;
          margin-bottom: 16px;
          flex-wrap: wrap;
        }

        .search-input-wrapper {
          position: relative;
          flex: 1;
          min-width: 300px;
          display: flex;
          align-items: center;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          background: white;
          padding: 0;
          overflow: hidden;
        }

        .search-icon-container {
          position: absolute;
          left: 12px;
          top: 50%;
          transform: translateY(-50%);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 2;
          pointer-events: none;
          color: #9ca3af;
        }

        .search-input {
          flex: 1;
          border: none;
          outline: none;
          padding: 12px 16px 12px 44px !important;
          font-size: 14px;
          background: transparent;
          color: #374151;
          text-align: left !important;
          width: 100%;
          box-sizing: border-box;
        }

        .search-input::placeholder {
          color: #9ca3af;
          text-align: left !important;
        }

        .search-input:focus {
          outline: none;
        }

        .search-input-wrapper:focus-within {
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .term-type-buttons {
          display: flex;
          gap: 4px;
          margin: 0 8px;
          flex-shrink: 0;
        }

        .term-type-btn {
          padding: 6px 12px;
          border: 1px solid #d1d5db;
          border-radius: 4px;
          background: white;
          color: #6b7280;
          font-size: 12px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
          white-space: nowrap;
        }

        .include-btn {
          background: #10b981;
          color: white;
          border-color: #10b981;
        }

        .include-btn:hover:not(:disabled) {
          background: #059669;
          border-color: #059669;
        }

        .exclude-btn {
          background: #ef4444;
          color: white;
          border-color: #ef4444;
        }

        .exclude-btn:hover:not(:disabled) {
          background: #dc2626;
          border-color: #dc2626;
        }

        .term-type-btn:disabled {
          background: #f3f4f6;
          color: #9ca3af;
          border-color: #d1d5db;
          cursor: not-allowed;
        }

        .tier-input-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          min-width: 180px;
          max-width: 200px;
          position: relative;
          flex-shrink: 0;
        }

        .tier-dropdown-container {
          position: relative;
          width: 100%;
          min-width: 180px;
        }

        .tier-dropdown-wrapper {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .tier-simple-select {
          flex: 1;
          padding: 12px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          background: white;
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 13px;
          color: #374151;
          font-weight: 600;
        }

        .tier-simple-select:hover {
          border-color: #9ca3af;
          background: #f9fafb;
        }

        .tier-simple-select:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .info-icon-container {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: help;
        }

        .info-icon {
          font-size: 16px;
          opacity: 0.7;
          transition: opacity 0.2s ease;
        }

        .info-icon-container:hover .info-icon {
          opacity: 1;
        }

        .tier-tooltip {
          position: absolute;
          top: calc(100% + 8px);
          right: 0;
          background: white;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          padding: 12px;
          z-index: 1000;
          min-width: 320px;
          max-width: 400px;
        }

        .tooltip-header {
          text-align: center;
          margin-bottom: 12px;
          font-size: 14px;
          color: #374151;
          border-bottom: 1px solid #e5e7eb;
          padding-bottom: 8px;
        }

        .tooltip-content {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .tooltip-tier {
          padding: 8px;
          border-radius: 6px;
          border: 1px solid #e5e7eb;
        }

        .tooltip-tier.warning {
          background: #fefce8;
          border-color: #fde047;
        }

        .tooltip-tier-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 4px;
        }

        .tooltip-tier-label {
          font-size: 13px;
          font-weight: 600;
          color: #374151;
        }

        .tooltip-tier-score {
          font-size: 12px;
          font-weight: 700;
          color: #059669;
          background: #dcfce7;
          padding: 2px 6px;
          border-radius: 4px;
        }

        .tooltip-tier.warning .tooltip-tier-score {
          color: #d97706;
          background: #fef3c7;
        }

        .tooltip-tier-meaning {
          font-size: 11px;
          color: #6b7280;
          line-height: 1.4;
          display: flex;
          align-items: flex-start;
          gap: 4px;
        }

        .tooltip-tier.warning .tooltip-tier-meaning {
          color: #92400e;
        }

        .warning-indicator {
          font-size: 10px;
          flex-shrink: 0;
          margin-top: 1px;
        }

        .tier-tooltip::after {
          content: '';
          position: absolute;
          bottom: 100%;
          right: 20px;
          border-left: 6px solid transparent;
          border-right: 6px solid transparent;
          border-bottom: 6px solid white;
        }

        .tier-tooltip::before {
          content: '';
          position: absolute;
          bottom: calc(100% + 1px);
          right: 20px;
          border-left: 6px solid transparent;
          border-right: 6px solid transparent;
          border-bottom: 6px solid #d1d5db;
        }

        .search-button-container {
  display: flex;
  align-items: flex-end;
  flex-shrink: 0;
}

.search-button {
  padding: 12px 20px;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
  min-width: 100px;
}

.search-button:hover:not(:disabled) {
  background: #2563eb;
}

.search-button:disabled {
  background: #d1d5db;
  color: #9ca3af;
  cursor: not-allowed;
}

        .loading-spinner-small {
          width: 16px;
          height: 16px;
          border: 2px solid transparent;
          border-top: 2px solid currentColor;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }

        .performance-filter-section {
          margin-bottom: 20px;
        }

        .performance-filter-row {
          display: flex;
          gap: 16px;
          align-items: flex-start;
          flex-wrap: wrap;
        }

        .sort-container {
          display: flex;
          flex-direction: column;
          gap: 6px;
          min-width: 120px;
          flex: 1;
        }

        .performance-label-container {
          display: flex;
          align-items: center;
          height: 20px;
        }

        .performance-filter-label {
          font-size: 12px;
          font-weight: 600;
          color: #374151;
          margin: 0;
          white-space: nowrap;
        }

        .sort-controls {
          display: flex;
          gap: 8px;
          align-items: center;
        }

        .sort-select {
          flex: 1;
          padding: 6px 8px;
          border: 1px solid #d1d5db;
          border-radius: 4px;
          font-size: 11px;
          background: white;
          color: #374151;
          outline: none;
          cursor: pointer;
        }

        .sort-select:focus {
          border-color: #1d4ed8;;
          box-shadow: 0 0 0 2px rgba(5, 150, 105, 0.1);
        }

        .sort-order-arrow {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 28px;
          height: 28px;
          background: #f3f4f6;
          border: 1px solid #d1d5db;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
          color: #374151;
          transition: all 0.2s ease;
          user-select: none;
          flex-shrink: 0;
        }

        .sort-order-arrow:hover {
          background: #e5e7eb;
          border-color: #9ca3af;
          color: #111827;
        }

        .quick-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding-top: 12px;
  border-top: 1px solid #e5e7eb;
  align-items: center;
}

.quick-filter {
  padding: 4px 8px;
  background: #f3f4f6;
  color: #6b7280;
  border-radius: 4px;
  font-size: 11px;
  border: 1px solid #e5e7eb;
  white-space: nowrap;
  flex-shrink: 0;


        .sort-indicator {
          background: #ecfdf5;
          color: #1d4ed8;
          border-color: #bbf7d0;
          font-weight: 600;
        }

        .filter-category {
          background: #eff6ff;
          color: #1d4ed8;
          border-color: #dbeafe;
        }

        .filter-platform {
          background: #f3e8ff;
          color: #7c3aed;
          border-color: #e9d5ff;
        }

        .filter-type {
          background: #fef3c7;
          color: #d97706;
          border-color: #fde68a;
        }

        .filter-license {
          background: #fce7f3;
          color: #be185d;
          border-color: #fbcfe8;
        }

        .filter-attributes {
          background: #ecfccb;
          color: #365314;
          border-color: #d9f99d;
        }

        .filter-metrics {
          background: #f0f9ff;
          color: #0c4a6e;
          border-color: #bae6fd;
        }

        .filter-financial {
          background: #fef2f2;
          color: #991b1b;
          border-color: #fecaca;
        }

        .filter-version {
          background: #f5f3ff;
          color: #5b21b6;
          border-color: #ddd6fe;
        }

        .error-display {
          padding: 12px 16px;
          background: #fee2e2;
          color: #991b1b;
          border: 1px solid #fecaca;
          border-radius: 8px;
          margin-top: 16px;
          font-size: 14px;
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .package-search-interface {
            padding: 16px;
          }

          .search-input-row {
            flex-direction: column;
            align-items: stretch;
          }

          .search-input-wrapper {
            min-width: auto;
          }

          .tier-input-container {
            width: 100%;
            align-items: stretch;
            min-width: auto;
            max-width: none;
          }

          .tier-dropdown-container {
            min-width: auto;
          }

          .tier-tooltip {
            position: fixed;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            right: auto;
            bottom: auto;
            width: calc(100vw - 32px);
            max-width: 350px;
            max-height: 60vh;
            overflow-y: auto;
          }

          .tier-tooltip::before,
          .tier-tooltip::after {
            display: none;
          }
        }
      `}</style>
		</div>
	);
};
